import type {
  ImpactAnalysisResult,
  ImpactLifecycleState,
  ImpactRootLifecycle,
  ImpactStateFact,
} from "../../api/impactAnalysis";
import type { CanonicalLineageChangeCause } from "../../api/canonicalLineage";
import type { DagImpactDisplayRole, DagViewerGraph } from "./types";

export type ImpactReachabilityStatus = "reachable";
export type ImpactPropagationStatus = "enabled" | "stopped" | "indeterminate";
export type ImpactExplicitInvalidationStatus = "recorded" | "not_recorded" | "indeterminate";
export type ImpactClassification =
  | "potential_only"
  | "explicitly_invalidated"
  | "stopped"
  | "indeterminate";

export interface ImpactPresentationHop {
  ordinal: number;
  inputArtifactVersionNodeId: string;
  inputBindingId: string;
  operationId: string;
  outputBindingId: string;
  outputArtifactVersionNodeId: string;
  inputRole: string;
  inputOrdinal: number;
  outputRole: string;
  outputOrdinal: number;
  propagatesInvalidity: boolean;
  bindingState: ImpactStateFact;
  operationState: ImpactStateFact;
}

export interface ImpactPresentationPath {
  pathId: string;
  subjectNodeId: string;
  propagationStatus: ImpactPropagationStatus;
  causes: CanonicalLineageChangeCause[];
  hops: ImpactPresentationHop[];
  terminalReason: string;
}

export interface ImpactPresentationReceipt {
  status: "attested" | "indeterminate" | "not_applicable";
  lifecycleEventId: string | null;
  auditEventId: string | null;
  domainEventId: string | null;
  idempotencyKey: string | null;
  requestHash: string | null;
  recordedAt: string | null;
  receiptKind: "native_invalidation" | "sealed_import_attestation" | null;
}

export interface ImpactPresentationSubject {
  ordinal: number;
  nodeId: string;
  artifactVersionId: string;
  label: string;
  artifactKind: string;
  lifecycleState: ImpactLifecycleState;
  reachabilityStatus: ImpactReachabilityStatus;
  propagationStatus: ImpactPropagationStatus;
  explicitInvalidationStatus: ImpactExplicitInvalidationStatus;
  classification: ImpactClassification;
  causes: CanonicalLineageChangeCause[];
  depth: number;
  pathIds: string[];
  exclusionIds: string[];
  explicitReason: string | null;
  explicitEffectiveAt: string | null;
  explicitRecordedAt: string | null;
  causedByArtifactVersionId: string | null;
  receipt: ImpactPresentationReceipt;
}

export interface ImpactPresentationGap {
  code: string;
  subjectNodeId: string | null;
}

export interface ImpactPresentationExclusion {
  exclusionId: string;
  subjectNodeId: string;
  pathId: string;
  reason: string;
  hopOrdinal: number;
}

export interface ImpactPresentationSummary {
  reachable: number;
  propagationEnabled: number;
  explicitlyInvalidated: number;
  potentialOnly: number;
  stopped: number;
  indeterminate: number;
}

export interface ImpactPresentation {
  projectId: string;
  rootNodeId: string;
  rootArtifactVersionId: string;
  rootLabel: string;
  rootLifecycle: ImpactRootLifecycle;
  effectiveAsOf: string;
  recordedAsOf: string;
  snapshotToken: string;
  semanticDigest: string;
  limits: ImpactAnalysisResult["document"]["snapshot"]["limits"];
  subjects: ImpactPresentationSubject[];
  paths: ImpactPresentationPath[];
  exclusions: ImpactPresentationExclusion[];
  gaps: ImpactPresentationGap[];
  summary: ImpactPresentationSummary;
  complete: boolean;
  redacted: boolean;
  warnings: string[];
}

export function impactDisplayRole(subject: ImpactPresentationSubject): DagImpactDisplayRole {
  if (subject.explicitInvalidationStatus === "recorded") return "impact-explicitly-invalidated";
  if (subject.propagationStatus === "indeterminate" || subject.explicitInvalidationStatus === "indeterminate") return "impact-indeterminate";
  if (subject.propagationStatus === "enabled") return "impact-propagation-enabled";
  if (subject.propagationStatus === "stopped") return "impact-stopped";
  return "impact-indeterminate";
}

function artifactVersionNodeId(id: string): string {
  return `artifact_version:${id}`;
}

function classification(
  propagation: ImpactPropagationStatus,
  explicit: ImpactExplicitInvalidationStatus,
): ImpactClassification {
  if (explicit === "recorded") return "explicitly_invalidated";
  if (propagation === "indeterminate" || explicit === "indeterminate") return "indeterminate";
  if (propagation === "enabled") return "potential_only";
  return "stopped";
}

function stateCauses(states: readonly ImpactStateFact[]): CanonicalLineageChangeCause[] {
  return [...new Set(states.map((state) => state.change_cause).filter((cause): cause is CanonicalLineageChangeCause => cause !== null))].sort();
}

/** Adapt only server-issued typed facts; the display graph is never consulted. */
export function buildImpactPresentation(result: ImpactAnalysisResult): ImpactPresentation {
  const document = result.document;
  const paths: ImpactPresentationPath[] = document.paths.map((path) => ({
    pathId: path.path_id,
    subjectNodeId: artifactVersionNodeId(path.subject_artifact_version_id),
    propagationStatus: path.propagation_status,
    causes: stateCauses(path.hops.flatMap((hop) => [hop.binding_state, hop.operation_state])),
    hops: path.hops.map((hop) => ({
      ordinal: hop.ordinal,
      inputArtifactVersionNodeId: artifactVersionNodeId(hop.input_artifact_version_id),
      inputBindingId: hop.input_binding_id,
      operationId: hop.operation_id,
      outputBindingId: hop.output_binding_id,
      outputArtifactVersionNodeId: artifactVersionNodeId(hop.output_artifact_version_id),
      inputRole: hop.input_role,
      inputOrdinal: hop.input_ordinal,
      outputRole: hop.output_role,
      outputOrdinal: hop.output_ordinal,
      propagatesInvalidity: hop.propagates_invalidity,
      bindingState: hop.binding_state,
      operationState: hop.operation_state,
    })),
    terminalReason: path.terminal.reason,
  }));
  const pathsBySubject = new Map<string, ImpactPresentationPath[]>();
  for (const path of paths) {
    const current = pathsBySubject.get(path.subjectNodeId) ?? [];
    current.push(path);
    pathsBySubject.set(path.subjectNodeId, current);
  }
  const subjects: ImpactPresentationSubject[] = document.subjects.map((subject) => {
    const nodeId = artifactVersionNodeId(subject.subject_ref.artifact_version_id);
    const evidence = subject.invalidation_evidence;
    const evidenceReceipt = evidence?.receipt ?? null;
    const receipt: ImpactPresentationReceipt = evidenceReceipt ? {
      status: "attested",
      lifecycleEventId: evidenceReceipt.lifecycle_event_id,
      auditEventId: evidenceReceipt.receipt_kind === "native_invalidation" ? evidenceReceipt.audit_event_id : null,
      domainEventId: evidenceReceipt.receipt_kind === "native_invalidation" ? evidenceReceipt.domain_event_id : null,
      idempotencyKey: evidenceReceipt.receipt_kind === "native_invalidation" ? evidenceReceipt.idempotency_key : null,
      requestHash: evidenceReceipt.receipt_kind === "native_invalidation" ? evidenceReceipt.request_hash : null,
      recordedAt: evidenceReceipt.recorded_at,
      receiptKind: evidenceReceipt.receipt_kind,
    } : {
      status: subject.explicit_invalidation_status === "indeterminate" ? "indeterminate" : "not_applicable",
      lifecycleEventId: null,
      auditEventId: null,
      domainEventId: null,
      idempotencyKey: null,
      requestHash: null,
      recordedAt: null,
      receiptKind: null,
    };
    const subjectPaths = pathsBySubject.get(nodeId) ?? [];
    return {
      ordinal: subject.ordinal,
      nodeId,
      artifactVersionId: subject.subject_ref.artifact_version_id,
      label: subject.subject_ref.artifact_name,
      artifactKind: subject.subject_ref.artifact_kind,
      lifecycleState: subject.subject_ref.lifecycle_state,
      reachabilityStatus: subject.reachability_status,
      propagationStatus: subject.propagation_status,
      explicitInvalidationStatus: subject.explicit_invalidation_status,
      classification: classification(subject.propagation_status, subject.explicit_invalidation_status),
      causes: [...new Set(subjectPaths.flatMap((path) => path.causes))].sort(),
      depth: subject.minimum_depth,
      pathIds: [...subject.path_ids],
      exclusionIds: [...subject.exclusion_ids],
      explicitReason: evidence?.reason ?? null,
      explicitEffectiveAt: evidence?.effective_at ?? null,
      explicitRecordedAt: evidence?.recorded_at ?? null,
      causedByArtifactVersionId: evidence?.caused_by_artifact_version_id ?? null,
      receipt,
    };
  });
  return {
    projectId: document.snapshot.project_id,
    rootNodeId: artifactVersionNodeId(document.snapshot.root_ref.id),
    rootArtifactVersionId: document.snapshot.root_ref.id,
    rootLabel: document.root_cause.artifact_name,
    rootLifecycle: document.root_lifecycle,
    effectiveAsOf: document.snapshot.effective_as_of,
    recordedAsOf: document.snapshot.recorded_as_of,
    snapshotToken: document.snapshot.snapshot_token,
    semanticDigest: result.semantic_identity.digest,
    limits: document.snapshot.limits,
    subjects,
    paths,
    exclusions: document.exclusions.map((entry) => ({
      exclusionId: entry.exclusion_id,
      subjectNodeId: artifactVersionNodeId(entry.subject_artifact_version_id),
      pathId: entry.path_id,
      reason: entry.reason,
      hopOrdinal: entry.step_ordinal,
    })),
    gaps: document.completeness.gaps.map((gap) => ({
      code: gap.code,
      subjectNodeId: gap.subject_artifact_version_id ? artifactVersionNodeId(gap.subject_artifact_version_id) : null,
    })),
    summary: {
      reachable: document.summary.reachable_count,
      propagationEnabled: document.summary.propagation_enabled_count,
      explicitlyInvalidated: document.summary.explicitly_invalidated_count,
      potentialOnly: document.summary.potential_only_count,
      stopped: document.summary.stopped_count,
      indeterminate: document.summary.indeterminate_count,
    },
    complete: document.completeness.traversal === "complete"
      && document.completeness.propagation === "complete"
      && document.completeness.lifecycle_evidence === "complete",
    redacted: document.completeness.redaction_terminal !== null,
    warnings: [],
  };
}

/**
 * Decorate the canonical downstream trace without turning trace membership into
 * an invalidity claim. Nodes absent from the typed result remain neutral,
 * explicitly-labelled reachable context.
 */
export function decorateImpactGraph(
  graph: DagViewerGraph,
  presentation: ImpactPresentation,
): DagViewerGraph {
  const roleByNodeId = new Map(
    presentation.subjects.map((subject) => [subject.nodeId, impactDisplayRole(subject)] as const),
  );
  return {
    ...graph,
    nodes: graph.nodes.map((node) => ({
      ...node,
      anchor: node.id === presentation.rootNodeId || node.anchor,
      metadata: {
        ...node.metadata,
        dag_view_role: node.id === presentation.rootNodeId
          ? "anchor-a"
          : roleByNodeId.get(node.id) ?? "impact-reachable",
      },
    })),
  };
}

/** Reachability-only canvas context while typed conclusions are pending/unavailable. */
export function decorateImpactContextGraph(graph: DagViewerGraph, rootNodeId: string): DagViewerGraph {
  return {
    ...graph,
    nodes: graph.nodes.map((node) => ({
      ...node,
      anchor: node.id === rootNodeId || node.anchor,
      metadata: {
        ...node.metadata,
        dag_view_role: node.id === rootNodeId ? "anchor-a" : "impact-reachable",
      },
    })),
  };
}
